import { useCallback, useEffect, useRef, useState, type MutableRefObject } from 'react'

import type { CoachConversation, CoachConversationFlow, CoachMessage } from '../../../../../../pure-js/types/CoachTypes'
import type { CoachVoiceTurn } from '../../../../../../pure-js/types/CoachApi'
import { CoachConversationMode, CoachMessageRole } from '../../../../../../pure-js/types/CoachTypes'
import { createUniqueId, isCallableError, toError } from '../../../../../../pure-js/libs/Common'
import {
  COACH_ROLE_BY_SPEAKER,
  meaningfulAriaTranscriptTurns,
  type AriaTranscriptTurn
} from '../../../../../../pure-js/libs/ariaTranscript'
import { resolveAssistantMessage } from '../../../../shared/components/AppAssistant/appAssistantModel'
import { useTranslation } from '../../../../shared/hooks/useIntractionTranslate'
import { sendErrorToSlack } from '../../../../libs/SlackHelper'
import {
  useCoachConversation,
  useSendCoachMessage,
  useStartCoachConversation
} from '../../../../apiLayer/queryHooks/coach'
import { useLostAnswerRecovery } from './useLostAnswerRecovery'

/** A locally-added bubble that has not been accepted by the backend yet. */
export type PendingCoachMessage = CoachMessage & { failed?: boolean }

export type UseCoachChatResult = {
  messages: PendingCoachMessage[]
  /**
   * What the turn could not tell the user itself: her reply is written before the goals are persisted,
   * so a goal the cap refused is invisible to her. Cleared on the next send.
   */
  notice?: string
  selectedId?: string
  /** The open conversation, so callers can tell a live thread from one that has been concluded. */
  selectedConversation?: CoachConversation
  draft: string
  isBusy: boolean
  setDraft: (value: string) => void
  selectConversation: (conversationId: string) => void
  /** Put the open conversation away, so the one to show next is picked afresh. */
  clearSelection: () => void
  /**
   * Defaults to a typed conversation; voice passes VOICE when it creates one to speak into. With
   * `resume`, the server hands back the most recent unfinished conversation rather than opening
   * another — so a repeated call cannot leave an empty thread behind.
   */
  startConversation: (
    mode?: CoachConversationMode,
    options?: { resume?: boolean; flow?: CoachConversationFlow }
  ) => Promise<string | undefined>
  /**
   * Send the draft. `conversationId` is passed when this send is what created the conversation — the
   * state holding it has not been applied yet at that moment.
   */
  sendDraft: (conversationId?: string) => Promise<void>
  retryMessage: (message: PendingCoachMessage) => Promise<void>
  /** Show what was just said out loud immediately, without waiting for the save to round-trip. */
  absorbSpokenTurns: (turns: CoachVoiceTurn[]) => void
  /** The turns of a live TYPED session, kept apart from `messages` because they are replaced whole on
   * every fragment. Pass an empty list to drop the tail once the saved thread has caught up. */
  setLiveTurns: (turns: AriaTranscriptTurn[]) => void
  /** Take the draft to send it over a live session, which does not round-trip through `sendDraft`. */
  takeDraft: () => string
}

const LOCAL_MESSAGE_PREFIX = 'local-'
/** Keeps a live turn's key distinct from an optimistic bubble's, so the two lists can never collide. */
const LIVE_MESSAGE_PREFIX = 'live-'

export type UseCoachChatParams = {
  /**
   * True while a spoken session is running. The saved thread is the subtitle history, and the spoken
   * turns are appended server-side as they are said, so adopting a mid-session refetch would show every
   * one of them twice — once from the server, once from the live socket.
   */
  isVoiceActiveRef: MutableRefObject<boolean>
}

/**
 * Owns the transcript, the composer and the send round-trip.
 *
 * A failed send must not lose what the user typed: the bubble stays, marked failed with a retry, and
 * the draft is restored. Onboarding goal-setting runs through this flow, so a silent drop would strand
 * a user with no way forward.
 */
export function useCoachChat({ isVoiceActiveRef }: UseCoachChatParams): UseCoachChatResult {
  const { t } = useTranslation()
  const startMutation = useStartCoachConversation()
  const sendMutation = useSendCoachMessage()

  const [selectedId, setSelectedId] = useState<string | undefined>(undefined)
  const [selectedConversation, setSelectedConversation] = useState<CoachConversation | undefined>(undefined)
  const [messages, setMessages] = useState<PendingCoachMessage[]>([])
  const [notice, setNotice] = useState<string | undefined>(undefined)
  const [draft, setDraft] = useState('')

  const { data: conversationDetail } = useCoachConversation(selectedId)
  const loadedConversation = conversationDetail?.conversation
  const loadedMessages = conversationDetail?.messages
  const appliedConversationIdRef = useRef<string | undefined>(undefined)
  const recoverLostReply = useLostAnswerRecovery()

  // Counted, because a message can be sent while she is still answering the one before.
  const [sendsInFlight, setSendsInFlight] = useState(0)
  const isSending = sendsInFlight > 0

  useEffect(() => {
    if (!loadedConversation || !loadedMessages) return
    setSelectedConversation(loadedConversation)

    // A different conversation always replaces the transcript. The same one only grows into it, and
    // never mid-send: spoken turns are appended server-side while the user talks, so the server can
    // legitimately hold more than we do — but adopting it during a send would drop the optimistic
    // bubble the user is watching.
    const isDifferentConversation = loadedConversation.id !== appliedConversationIdRef.current
    const serverHasMore = loadedMessages.length > messages.length
    if (!isDifferentConversation && (isSending || isVoiceActiveRef.current || !serverHasMore)) return

    appliedConversationIdRef.current = loadedConversation.id
    setMessages(loadedMessages)
    // `messages` is read, not tracked: reacting to our own write would re-run this on every send.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadedConversation, loadedMessages, isSending, isVoiceActiveRef])

  const selectConversation = useCallback((conversationId: string) => setSelectedId(conversationId), [])

  const clearSelection = useCallback(() => {
    appliedConversationIdRef.current = undefined
    setSelectedId(undefined)
    setSelectedConversation(undefined)
    setMessages([])
    setNotice(undefined)
  }, [])

  const startConversation = useCallback(
    async (
      mode: CoachConversationMode = CoachConversationMode.TEXT,
      options?: { resume?: boolean; flow?: CoachConversationFlow }
    ) => {
      try {
        const response = await startMutation.mutateAsync({ mode, resume: options?.resume, flow: options?.flow })
        appliedConversationIdRef.current = response.data.conversation.id
        setSelectedId(response.data.conversation.id)
        setSelectedConversation(response.data.conversation)
        setMessages(response.data.messages)
        return response.data.conversation.id
      } catch (error) {
        // Only a fault of the page itself is reported; a failed start can simply be tried again.
        if (!isCallableError(error)) void sendErrorToSlack(toError(error), undefined, 'useCoachChat.startConversation')
        return undefined
      }
    },
    [startMutation]
  )

  // Her reply joins the thread, and the bubble it answers counts as sent.
  const acceptReply = useCallback((optimisticId: string, reply: CoachMessage) => {
    setMessages((current) => [
      ...current.map((message) => (message.id === optimisticId ? { ...message, failed: false } : message)),
      reply
    ])
  }, [])

  // The bubble's id travels with the message, so a retry or a lost answer is matched back to it.
  const deliver = useCallback(
    async (conversationId: string, content: string, optimisticId: string) => {
      const sentAt = Date.now()
      setSendsInFlight((count) => count + 1)

      try {
        const response = await sendMutation.mutateAsync({ conversationId, content, clientMessageId: optimisticId })
        acceptReply(optimisticId, response.data.reply)
        setNotice(response.data.notice ? resolveAssistantMessage(t, response.data.notice) : undefined)
      } catch (error) {
        // An answer lost on its way back can belong to a message she stored and answered; then that reply stands.
        const storedReply = await recoverLostReply(error, { conversationId, clientMessageId: optimisticId, sentAt })
        if (storedReply) {
          acceptReply(optimisticId, storedReply)
          return
        }

        // Keep the bubble, flag it, and hand the text back so the user can retry or edit.
        setMessages((current) =>
          current.map((message) => (message.id === optimisticId ? { ...message, failed: true } : message))
        )
        setDraft((current) => current || content)
        // Only a fault of the page itself is reported; a failed call is left to the bubble's retry.
        if (!isCallableError(error)) void sendErrorToSlack(toError(error), undefined, 'useCoachChat.sendMessage')
      } finally {
        setSendsInFlight((count) => count - 1)
      }
    },
    [acceptReply, recoverLostReply, sendMutation, t]
  )

  const sendDraft = useCallback(
    async (conversationId?: string) => {
      const content = draft.trim()
      /*
       * The id is passed in when this very send is what created the conversation. `selectedId` is
       * state, so it is still undefined in this closure at that point — reading it from state here
       * dropped the user's first message on the floor without a word.
       */
      const targetId = conversationId || selectedId
      if (!content || !targetId) return

      const optimistic: PendingCoachMessage = {
        id: `${LOCAL_MESSAGE_PREFIX}${createUniqueId()}`,
        // ISO-8601 UTC, the same format the backend stamps messages with.
        createdAt: new Date().toISOString(),
        conversationId: targetId,
        role: CoachMessageRole.USER,
        content
      }
      setDraft('')
      setNotice(undefined)
      setMessages((current) => [...current, optimistic])
      await deliver(targetId, content, optimistic.id)
    },
    [deliver, draft, selectedId]
  )

  // Local echo of the spoken turns. The server copy arrives moments later and is adopted only when it
  // holds more than this, so the two can never stack up into a doubled transcript.
  const absorbSpokenTurns = useCallback(
    (turns: CoachVoiceTurn[]) => {
      if (turns.length === 0 || !selectedId) return
      const now = new Date().toISOString()
      setMessages((current) => [
        ...current,
        ...turns.map((turn) => ({
          id: `${LOCAL_MESSAGE_PREFIX}${createUniqueId()}`,
          createdAt: now,
          conversationId: selectedId,
          role: turn.role,
          content: turn.content,
          fromVoice: true
        }))
      ])
    },
    [selectedId]
  )

  // Its own list because it is replaced wholesale on every fragment, while `messages` is the settled
  // transcript. The ids are the transcript's own turn ids, so React re-keys nothing as a turn grows.
  const [liveTurnMessages, setLiveTurnMessages] = useState<PendingCoachMessage[]>([])
  const setLiveTurns = useCallback(
    (turns: AriaTranscriptTurn[]) => {
      // Clearing must always be possible: gated on a selected conversation, the tail would strand on
      // screen when the session ends with the selection, and the saved thread would then double it.
      if (!turns.length) {
        setLiveTurnMessages((current) => (current.length ? [] : current))
        return
      }
      if (!selectedId) return
      const now = new Date().toISOString()
      setLiveTurnMessages(
        meaningfulAriaTranscriptTurns(turns).map((turn) => ({
          id: `${LIVE_MESSAGE_PREFIX}${turn.id}`,
          createdAt: now,
          conversationId: selectedId,
          role: COACH_ROLE_BY_SPEAKER[turn.role],
          content: turn.text.trim()
        }))
      )
    },
    [selectedId]
  )

  const takeDraft = useCallback(() => {
    const content = draft.trim()
    if (content) setDraft('')
    return content
  }, [draft])

  const retryMessage = useCallback(
    async (message: PendingCoachMessage) => {
      if (!selectedId) return
      setMessages((current) => current.map((entry) => (entry.id === message.id ? { ...entry, failed: false } : entry)))
      await deliver(selectedId, message.content, message.id)
    },
    [deliver, selectedId]
  )

  return {
    messages: liveTurnMessages.length ? [...messages, ...liveTurnMessages] : messages,
    notice,
    selectedId,
    selectedConversation,
    draft,
    isBusy: isSending || startMutation.isPending,
    setDraft,
    selectConversation,
    clearSelection,
    startConversation,
    sendDraft,
    retryMessage,
    absorbSpokenTurns,
    setLiveTurns,
    takeDraft
  }
}
